// Super admin area (/admin): accounts, device stock + allocation (D7/D8), gateway fleet health. All writes are
// allowed to super admins only by RLS / move_device().
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeviceCategory } from '../lib/devices';
import { supabase } from '../lib/supabase';

export interface CompanyOverview {
  company_id: string;
  name: string;
  code: string;
  kind: 'platform' | 'customer';
  is_active: boolean;
  timezone: string;
  created_at: string;
  members: number;
  managers: number;
  sites: number;
  assets: number;
  tags: number;
  gateways: number;
  gateways_online: number;
}

export type DeviceKind = 'tag' | 'gateway';

/** A hardware model in the super admin's catalogue (public.device_types). */
export interface DeviceType {
  id: string;
  category: DeviceCategory;
  manufacturer: string;
  model: string;
  traccar_protocol: string | null;
  note: string | null;
  is_active: boolean;
}

type DeviceTypeRef = Pick<DeviceType, 'id' | 'manufacturer' | 'model'>;

export interface DeviceRow {
  kind: DeviceKind;
  id: string;
  type: DeviceCategory;
  device_type: DeviceTypeRef | null;
  identifier: string;
  status: 'in_stock' | 'allocated' | 'faulty' | 'retired';
  company_id: string;
  company: string;
  attached: string | null;
  sim_iccid: string | null;
  last_seen_at: string | null;
}

export interface GatewayHealth {
  id: string;
  identifier: string;
  display_name: string | null;
  is_online: boolean;
  last_seen_at: string | null;
  online_since: string | null;
  status: string;
  companies: { name: string; code: string } | null;
  sites: { name: string } | null;
}

export interface Movement {
  id: number;
  from_status: string | null;
  to_status: string;
  reason: string | null;
  moved_at: string;
  from_company: { code: string } | null;
  to_company: { code: string } | null;
}

async function data<T>(
  q: PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>,
): Promise<T> {
  const { data: d, error } = await q;
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return d as T;
}

export const adminKeys = {
  companies: ['admin', 'companies'] as const,
  company: (id: string) => ['admin', 'company', id] as const,
  members: (id: string) => ['admin', 'members', id] as const,
  devices: ['admin', 'devices'] as const,
  deviceTypes: ['admin', 'device-types'] as const,
  gateways: ['admin', 'gateway-health'] as const,
  movements: (kind: DeviceKind, id: string) => ['admin', 'movements', kind, id] as const,
  superAdmins: ['admin', 'super-admins'] as const,
  superAdminChanges: ['admin', 'super-admin-changes'] as const,
};

/** A Fleetspoke super admin (admin_platform_admins). */
export interface SuperAdmin {
  user_id: string;
  full_name: string | null;
  phone: string | null;
  email: string | null;
  last_sign_in_at: string | null;
  /** user_activity (whoami stamps it while the app is open) */
  last_active_at: string | null;
  added_at: string;
  added_by_name: string | null;
  is_me: boolean;
}

/** Add last_active_at from user_activity (a super admin reads every row) to people listed by an admin RPC. */
async function withActivity<T extends { user_id: string }>(
  people: T[],
): Promise<(T & { last_active_at: string | null })[]> {
  const ids = [...new Set(people.map((p) => p.user_id))];
  const rows = ids.length
    ? await data<{ user_id: string; last_active_at: string }[]>(
        supabase().from('user_activity').select('user_id, last_active_at').in('user_id', ids),
      )
    : [];
  const at = new Map(rows.map((r) => [r.user_id, r.last_active_at]));
  return people.map((p) => ({ ...p, last_active_at: at.get(p.user_id) ?? null }));
}

/** One addition or removal (platform_admin_changes; written only by the two RPCs). */
export interface SuperAdminChange {
  id: string;
  action: 'added' | 'removed';
  subject_name: string | null;
  by_name: string | null;
  at: string;
}

export function useSuperAdmins() {
  return useQuery({
    queryKey: adminKeys.superAdmins,
    queryFn: async (): Promise<SuperAdmin[]> =>
      withActivity(
        await data<Omit<SuperAdmin, 'last_active_at'>[]>(supabase().rpc('admin_platform_admins')),
      ),
  });
}

export function useSuperAdminChanges() {
  return useQuery({
    queryKey: adminKeys.superAdminChanges,
    queryFn: () =>
      data<SuperAdminChange[]>(
        supabase()
          .from('platform_admin_changes')
          .select('id, action, subject_name, by_name, at')
          .order('at', { ascending: false })
          .limit(50),
      ),
  });
}

/** Add a super admin through invite-user (kind platform_admin): the account if needed, then add_platform_admin. */
export async function inviteSuperAdmin(p: NewCompany['manager']): Promise<string | null> {
  const { error } = await supabase().functions.invoke('invite-user', {
    body: {
      kind: 'platform_admin',
      phone: p.phone,
      email: p.email.trim() || undefined,
      full_name: p.full_name.trim() || undefined,
      locale: p.locale,
    },
  });
  if (!error) return null;
  return (await functionErrorCode(error)) ?? 'unavailable';
}

/** Never yourself; the database also keeps the last one and closes the removed admin's open support visit. */
export function useRemoveSuperAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      data(supabase().rpc('remove_platform_admin', { p_user: userId })),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.superAdmins });
      void queryClient.invalidateQueries({ queryKey: adminKeys.superAdminChanges });
    },
  });
}

export function useCompanies() {
  return useQuery({
    queryKey: adminKeys.companies,
    queryFn: () =>
      data<CompanyOverview[]>(
        supabase().from('company_overview').select('*').order('kind').order('name'),
      ),
  });
}

export function useCompany(id: string) {
  return useQuery({
    queryKey: adminKeys.company(id),
    queryFn: () =>
      data<CompanyOverview | null>(
        supabase().from('company_overview').select('*').eq('company_id', id).maybeSingle(),
      ),
  });
}

/** Name and timezone (the code never changes — S4, enforced by the database). */
export function useUpdateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, timezone }: { id: string; name: string; timezone: string }) =>
      data(supabase().from('companies').update({ name: name.trim(), timezone }).eq('id', id)),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export interface CompanyMember {
  membership_id: string;
  user_id: string;
  full_name: string | null;
  phone: string | null;
  email: string | null;
  locale: 'en' | 'ms' | 'zh';
  role: 'manager' | 'warehouse_staff' | 'project_user';
  site_access: 'all' | 'selected';
  is_active: boolean;
  invited_at: string;
  invited_by_name: string | null;
  last_sign_in_at: string | null;
  last_active_at: string | null;
  invite_state: 'invited' | 'active' | 'deactivated';
}

export function useCompanyMembers(companyId: string) {
  return useQuery({
    queryKey: adminKeys.members(companyId),
    queryFn: async (): Promise<CompanyMember[]> =>
      withActivity(
        await data<Omit<CompanyMember, 'last_active_at'>[]>(
          supabase().rpc('admin_company_members', { p_company: companyId }),
        ),
      ),
  });
}

/** Delete a deactivated membership row and the person's grants in that account (their work and account stay). */
export function useDeleteMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) =>
      data(supabase().rpc('admin_delete_member', { p_membership: membershipId })),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

/** Promote to manager and/or (de)activate (D6a). Moving a manager to staff is set_member_access, in Settings →
 *  Users. The database refuses leaving an active company without a manager (S5). */
export function useSetMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { membershipId: string; role?: 'manager'; active?: boolean }) =>
      data(
        supabase().rpc('admin_set_member', {
          p_membership: input.membershipId,
          p_role: input.role ?? null,
          p_active: input.active ?? null,
        }),
      ),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useDevices() {
  return useQuery({
    queryKey: adminKeys.devices,
    queryFn: async () => {
      const [tags, gateways] = await Promise.all([
        data<
          {
            id: string;
            type: DeviceCategory;
            identifier: string;
            status: DeviceRow['status'];
            company_id: string;
            sim_iccid: string | null;
            last_seen_at: string | null;
            companies: { code: string } | null;
            assets: { asset_code: string } | null;
            device_types: DeviceTypeRef | null;
          }[]
        >(
          supabase()
            .from('tags')
            .select(
              'id, type, identifier, status, company_id, sim_iccid, last_seen_at, companies(code), assets(asset_code), device_types(id, manufacturer, model)',
            )
            .in('type', ['ble_beacon', 'ble_sensor', 'gps_tracker'])
            .order('identifier'),
        ),
        data<
          {
            id: string;
            type: string;
            identifier: string;
            status: DeviceRow['status'];
            company_id: string;
            sim_iccid: string | null;
            last_seen_at: string | null;
            companies: { code: string } | null;
            sites: { name: string } | null;
            device_types: DeviceTypeRef | null;
          }[]
        >(
          supabase()
            .from('gateways')
            .select(
              'id, type, identifier, status, company_id, sim_iccid, last_seen_at, companies(code), sites(name), device_types(id, manufacturer, model)',
            )
            .eq('type', 'fixed')
            .order('identifier'),
        ),
      ]);
      return [
        ...tags.map((t): DeviceRow => ({
          kind: 'tag',
          id: t.id,
          type: t.type,
          device_type: t.device_types,
          identifier: t.identifier,
          status: t.status,
          company_id: t.company_id,
          company: t.companies?.code ?? '',
          attached: t.assets?.asset_code ?? null,
          sim_iccid: t.sim_iccid,
          last_seen_at: t.last_seen_at,
        })),
        ...gateways.map((g): DeviceRow => ({
          kind: 'gateway',
          id: g.id,
          type: 'fixed_gateway',
          device_type: g.device_types,
          identifier: g.identifier,
          status: g.status,
          company_id: g.company_id,
          company: g.companies?.code ?? '',
          attached: g.sites?.name ?? null,
          sim_iccid: g.sim_iccid,
          last_seen_at: g.last_seen_at,
        })),
      ];
    },
  });
}

export function useGatewayHealth() {
  return useQuery({
    queryKey: adminKeys.gateways,
    queryFn: () =>
      data<GatewayHealth[]>(
        supabase()
          .from('gateways')
          .select(
            'id, identifier, display_name, is_online, last_seen_at, online_since, status, companies(name, code), sites(name)',
          )
          .eq('type', 'fixed')
          .eq('status', 'allocated')
          .order('is_online')
          .order('last_seen_at', { ascending: true, nullsFirst: true }),
      ),
    refetchInterval: 60_000,
  });
}

/** One device's allocation / return history (D8c), newest first — the Logs row under the device. */
export function useDeviceMovements(kind: DeviceKind, id: string) {
  return useQuery({
    queryKey: adminKeys.movements(kind, id),
    queryFn: () =>
      data<Movement[]>(
        supabase()
          .from('device_movements')
          .select(
            'id, from_status, to_status, reason, moved_at, from_company:companies!device_movements_from_company_id_fkey(code), to_company:companies!device_movements_to_company_id_fkey(code)',
          )
          .eq('device_kind', kind)
          .eq('device_id', id)
          .order('moved_at', { ascending: false }),
      ),
  });
}

/** Error code from an edge function answer ({error: code}), or undefined. */
export async function functionErrorCode(error: unknown): Promise<string | undefined> {
  if (!(error instanceof FunctionsHttpError)) return undefined;
  const body = (await error.context.json().catch(() => ({}))) as { error?: string };
  return body.error;
}

export interface NewCompany {
  name: string;
  code: string;
  timezone: string;
  manager: { full_name: string; phone: string; email: string; locale: 'en' | 'ms' | 'zh' };
}

/** Create the account, then invite its first manager (invite-user; nothing is sent — they are told offline). */
export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewCompany) => {
      const [company] = await data<{ id: string }[]>(
        supabase()
          .from('companies')
          .insert({
            name: input.name.trim(),
            code: input.code.trim().toUpperCase(),
            timezone: input.timezone,
          })
          .select('id'),
      );
      if (!company) throw new Error('company not created');
      const invited = await inviteManager(company.id, input.manager);
      return { companyId: company.id, invited };
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: adminKeys.companies }),
  });
}

export async function inviteManager(
  companyId: string,
  m: NewCompany['manager'],
): Promise<string | null> {
  const { error } = await supabase().functions.invoke('invite-user', {
    body: {
      company_id: companyId,
      role: 'manager',
      site_access: 'all',
      phone: m.phone,
      email: m.email.trim() || undefined,
      full_name: m.full_name.trim() || undefined,
      locale: m.locale,
    },
  });
  if (!error) return null;
  return (await functionErrorCode(error)) ?? 'unavailable';
}

export function useSetCompanyActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      data(supabase().from('companies').update({ is_active: active }).eq('id', id)),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: adminKeys.companies }),
  });
}

export function useDeviceTypes() {
  return useQuery({
    queryKey: adminKeys.deviceTypes,
    queryFn: () =>
      data<DeviceType[]>(
        supabase()
          .from('device_types')
          .select('id, category, manufacturer, model, traccar_protocol, note, is_active')
          .order('manufacturer')
          .order('model'),
      ),
  });
}

export type DeviceTypeInput = Omit<DeviceType, 'id'> & { id?: string };

/** Add (no id) or edit a device type. The database refuses a duplicate manufacturer + model (23505) and a
 * category change while devices use the type (55000). */
export function useSaveDeviceType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: DeviceTypeInput) => {
      const row = {
        ...input,
        manufacturer: input.manufacturer.trim(),
        model: input.model.trim(),
        traccar_protocol: input.traccar_protocol?.trim().toLowerCase() || null,
        note: input.note?.trim() || null,
      };
      return data(
        id
          ? supabase().from('device_types').update(row).eq('id', id)
          : supabase().from('device_types').insert(row),
      );
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

/** Delete an unused device type (a type in use is archived instead; the FK refuses with 23503). */
export function useDeleteDeviceType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => data(supabase().from('device_types').delete().eq('id', id)),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

/** Correct a device's type (same category — the database checks). */
export function useSetDeviceType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: DeviceKind; id: string; deviceTypeId: string }) =>
      data(
        supabase()
          .from(input.kind === 'tag' ? 'tags' : 'gateways')
          .update({ device_type_id: input.deviceTypeId })
          .eq('id', input.id),
      ),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

/** Add devices of one type to FleetSpoke stock (the platform account), in_stock. */
export function useAddDevices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      platformId: string;
      deviceType: Pick<DeviceType, 'id' | 'category'>;
      identifiers: string[];
      sim: string | null;
    }) => {
      const gateway = input.deviceType.category === 'fixed_gateway';
      const rows = input.identifiers.map((identifier) => ({
        company_id: input.platformId,
        identifier,
        status: 'in_stock',
        sim_iccid: input.sim,
        device_type_id: input.deviceType.id,
        type: gateway ? 'fixed' : input.deviceType.category,
      }));
      await data(
        supabase()
          .from(gateway ? 'gateways' : 'tags')
          .insert(rows),
      );
      return rows.length;
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

/** Allocate to a customer, return to stock, mark faulty / retired (move_device logs every movement). */
export function useMoveDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      kind: DeviceKind;
      id: string;
      toCompanyId: string;
      toStatus?: 'in_stock' | 'faulty' | 'retired';
      reason?: string;
    }) =>
      data(
        supabase().rpc('move_device', {
          p_device_kind: input.kind,
          p_device_id: input.id,
          p_to_company_id: input.toCompanyId,
          p_to_status: input.toStatus ?? null,
          p_reason: input.reason ?? null,
        }),
      ),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}
